// Copied from platform-app components/pos/layout/ButtonAuthorizationTab.tsx on 2026-10-18 for Super Admin → POS Defaults — keep in step with the original.
import React from 'react';
import { ShieldCheck, Lock } from 'lucide-react';
import { BUTTON_REGISTRY, type ButtonOverride } from '../../../config/buttonRegistry';
import { getIcon } from '../../../utils/iconRegistry';

/**
 * Button Authorization — every till button in one list: who may use it and whether
 * a POS admin (store manager) must enter their PIN first. Writes the same
 * `visibility` / `requiresApproval` fields as the Customize Button popup.
 *
 * Void is special on the till: unset means a manager is needed (it always was), and
 * the role's Void Approval Threshold lets small voids through without one.
 */

/** Buttons that need a manager PIN in the recommended setup. Mirrors super-admin-ext2 default-register-layout. */
export const RECOMMENDED_APPROVAL_IDS = ['void', 'refund', 'exchange', 'discount', 'open-drawer', 'damage', 'payout'];

export function approvalOn(id: string, cust: ButtonOverride | undefined): boolean {
  return id === 'void' ? cust?.requiresApproval !== false : cust?.requiresApproval === true;
}

const GROUPS: { id: string; label: string }[] = [
  { id: 'sales', label: 'Sales' },
  { id: 'register', label: 'Register & cash' },
  { id: 'employee', label: 'Employee' },
  { id: 'admin', label: 'Admin' },
];

const VISIBILITY: { value: NonNullable<ButtonOverride['visibility']>; label: string }[] = [
  { value: 'all', label: 'Everyone' },
  { value: 'admin', label: 'Manager only' },
  { value: 'employee', label: 'Employee only' },
];

interface Props {
  buttonIds: string[];
  customizations: Record<string, ButtonOverride>;
  onChange: (updater: (prev: Record<string, ButtonOverride>) => Record<string, ButtonOverride>) => void;
}

const ButtonAuthorizationTab: React.FC<Props> = ({ buttonIds, customizations, onChange }) => {
  const ids = buttonIds.filter((id, i) => BUTTON_REGISTRY[id] && buttonIds.indexOf(id) === i);

  const patch = (id: string, p: Partial<ButtonOverride>) =>
    onChange((prev) => ({ ...prev, [id]: { ...prev[id], ...p } }));

  const applyRecommended = () =>
    onChange((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = { ...next[id], requiresApproval: RECOMMENDED_APPROVAL_IDS.includes(id) };
      return next;
    });

  const approvalCount = ids.filter((id) => approvalOn(id, customizations[id])).length;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-stone-800 text-sm font-semibold flex items-center gap-2">
            <ShieldCheck size={16} className="text-accent-600" /> Button Authorization
          </h3>
          <p className="text-stone-500 text-xs mt-1 max-w-xl">
            Manager PIN = a store manager enters their PIN before the cashier can go on. Store managers are never asked.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-stone-500 tabular-nums">{approvalCount} need a manager</span>
          <button
            type="button"
            onClick={applyRecommended}
            className="min-h-[44px] px-4 rounded-lg border border-stone-300 bg-white text-sm font-semibold text-stone-700 hover:bg-stone-50"
          >
            Use recommended
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {GROUPS.map((g) => {
          const rows = ids.filter((id) => BUTTON_REGISTRY[id].group === g.id);
          if (rows.length === 0) return null;
          return (
            <div key={g.id}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-1.5">{g.label}</div>
              <div className="divide-y divide-stone-100 rounded-xl border border-stone-200">
                {rows.map((id) => {
                  const reg = BUTTON_REGISTRY[id];
                  const cust = customizations[id];
                  const Icon = getIcon(cust?.icon || reg.icon);
                  const vis = cust?.visibility ?? 'all';
                  const needs = approvalOn(id, cust);
                  const off = cust?.active === false;
                  return (
                    <div key={id} className={`flex flex-wrap items-center gap-3 px-3 py-2 ${off ? 'opacity-50' : ''}`}>
                      <div className="flex items-center gap-2.5 min-w-[160px] flex-1">
                        <span className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                          <Icon size={16} className="text-stone-700" />
                        </span>
                        <div>
                          <div className="text-sm font-medium text-stone-800">{cust?.label || reg.label}</div>
                          {id === 'void' && <div className="text-[11px] text-stone-400">Above each role's void threshold</div>}
                          {off && <div className="text-[11px] text-stone-400">Turned off on this layout</div>}
                        </div>
                      </div>

                      <div className="flex rounded-lg border border-stone-200 bg-stone-50 p-0.5" role="group" aria-label={`${reg.label} visibility`}>
                        {VISIBILITY.map((v) => (
                          <button
                            key={v.value}
                            type="button"
                            aria-pressed={vis === v.value}
                            onClick={() => patch(id, { visibility: v.value })}
                            className={`min-h-[40px] px-3 rounded-md text-xs font-semibold transition-colors ${
                              vis === v.value ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                            }`}
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={needs}
                        onClick={() => patch(id, { requiresApproval: !needs })}
                        className="flex items-center gap-2 min-h-[44px] pl-2"
                      >
                        <Lock size={14} className={needs ? 'text-stone-800' : 'text-stone-300'} />
                        <span className="text-xs font-semibold text-stone-600 w-[86px] text-left">Manager PIN</span>
                        <span className={`relative w-10 h-6 rounded-full transition-colors ${needs ? 'bg-accent-500' : 'bg-stone-300'}`}>
                          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${needs ? 'left-[18px]' : 'left-0.5'}`} />
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ButtonAuthorizationTab;
